import { httpClient } from '@texas/api/httpClient/httpClient';

export interface OfferTierPrice {
  id: number;
  basedOnId: number | null;
  basedOnPrice: number | null;
  quantity: number;
  price: number | null;
  acceptedDate: string | null;
  acceptedByUserName: string | null;
  isInOfferedOffer: string | null;
}

interface OfferGroupTierDetail {
  id: number;
  quantity: number;
  archived: boolean;
}

export interface OfferGroupDetail {
  id: number;
  articleId: number;
  articleItemCode: string;
  customerId: number;
  ownerId: number;
  ownerName: string;
  created: string;
  isMain: boolean;
  archived: boolean;
  tiers: OfferGroupTierDetail[];
  targetTierQuantities: number[];
  inquiryTierQuantities: number[];
  branches: OfferGroupBranch[];
  missingBranches: MissingBranchDetail[];
}

interface InquiryTierPriceAlignment {
  quantity: number;
  matrixNodeValueXId: number | null;
  matrixNodeValueYId: number | null;
  price: number | null;
}

export interface AcceptedInquiryAlignment {
  inquiryId: number;
  inquiryNumber: string;
  supplierName: string;
  isMain: boolean;
  isMatched: boolean;
  exchangeRate: number;
  sampleCharge: number;
  mouldCharge: number;
  surcharge: number;
  tierPrices: InquiryTierPriceAlignment[];
}

export interface OfferGroupBranch {
  branchId: number;
  branchName: string;
  branchIdentifier: string;
  branchDefaultCurrencyId: number;
  currencies: OfferGroupBranchCurrency[];
  acceptedInquiries: AcceptedInquiryAlignment[];
}

export interface MissingBranchDetail {
  branchId: number;
  branchName: string;
  branchIdentifier: string;
  branchDefaultCurrencyId: number;
  acceptedInquiries: AcceptedInquiryAlignment[];
}

interface MissingCell {
  xId: number;
  yId: number;
  xName: string;
  yName: string;
}

export interface OfferGroupBranchCurrency {
  currencyId: number;
  masterOffer: Offer | null;
  childOffers: Offer[];
  missingCellCount: number;
  missingCells: MissingCell[];
}

export interface Offer {
  id: number;
  inquiryId: number;
  groupId: string;
  offerGroupId: number | null;
  basedOnId: number | null;
  controlledById: number | null;
  exchangeRate: number;
  currencyId: number;
  sampleCharge: number;
  mouldCharge: number;
  surcharge: number;
  mpq: number | null;
  matrixNodeValueXId: number | null;
  matrixNodeValueYId: number | null;
  matrixNodeValueXName: string | null;
  matrixNodeValueYName: string | null;
  supplierContactId: number | null;
  createdByUserId: number;
  created: string;
  archived: string | null;
  isMain: boolean;
  branchId: number;
  tierPrices: OfferTierPrice[];
}

interface CreateOfferRequest {
  variantId: number;
  inquiryIds: number[];
}

export interface UpdateOfferRequest {
  exchangeRate: number;
  sampleCharge: number;
  mouldCharge: number;
  surcharge: number;
  supplierContactId?: number | null;
  mpq?: number | null;
}

export interface OfferPreview {
  isMatrix: boolean;
  matrixXCount: number;
  matrixYCount: number;
  branches: PreviewBranch[];
  targetTiers: PreviewTier[];
  acceptedInquiries: PreviewAcceptedInquiry[];
}

interface PreviewBranch {
  branchId: number;
  name: string;
  identifier: string;
  currencyCode: string;
}

interface PreviewTier {
  quantity: number;
}

export interface PreviewAcceptedInquiry {
  id: number;
  branchId: number;
  branchIdentifier: string;
  inquiryNumber: string;
  supplierName: string;
  acceptedDate: string;
  isMain: boolean;
  tiers: PreviewTier[];
}

export interface OfferGroupSummary {
  id: number;
  isMain: boolean;
  archived: boolean;
  created: string;
  creatorName: string;
  offerNumbers: string[];
  tiers: OfferGroupTierSummary[];
  coveredBranches: BranchSummary[];
  missingBranches: BranchSummary[];
  branchCurrencies: BranchCurrencySummary[];
}

interface OfferGroupTierSummary {
  quantity: number;
}

export interface BranchSummary {
  branchId: number;
  name: string;
  identifier: string;
}

export interface BranchCurrencySummary {
  branchId: number;
  branchIdentifier: string;
  currencyId: number;
  currencyCode: string;
  exchangeRate: number;
  acceptedPriceCount: number;
  offeredPriceCount: number;
  totalPriceCount: number;
}

interface OfferedOfferTierPrice {
  id: number;
  quantity: number;
  price: number | null;
  acceptedDate: string | null;
  acceptedByUserName: string | null;
  matrixNodeValueXName: string | null;
  matrixNodeValueYName: string | null;
  carryOverOfferNumber: string | null;
  carryOverExpiresOn: string | null;
}

export interface OfferedOfferCurrency {
  currencyId: number;
  currencyCode: string;
  tierPrices: OfferedOfferTierPrice[];
}

export interface OfferedOfferBranch {
  branchId: number;
  branchName: string;
  branchIdentifier: string;
  currencies: OfferedOfferCurrency[];
}

interface OfferedOfferPreview {
  offerGroupId: number;
  customerId: number;
  customerName: string;
  defaultContactId: number | null;
  branches: OfferedOfferBranch[];
}

export interface OfferedOffer {
  id: number;
  offerGroupId: number;
  articleId: number;
  variantId: number;
  offerNumber: string;
  basedOnOfferedOfferId: number | null;
  basedOnOfferedOfferNumber: string | null;
  customerId: number;
  customerName: string;
  contactId: number;
  contactName: string;
  contactEmail: string;
  addedOn: string;
  sentOn: string;
  expiresOn: string;
  cancelledAt: string | null;
  cancelledByName: string | null;
  ownerName: string;
  tierPriceCount: number;
  acceptedTierPriceCount: number;
  branches: OfferedOfferBranch[];
}

export interface UnfinishedOfferGroup {
  offerGroupId: number;
  articleId: number;
  variantId: number;
  customerId: number;
  customerName: string;
  created: string;
  branchCount: number;
  branchIdentifiers: string[];
  tierPriceCount: number;
}

export interface OffersWidgetData {
  offeredOffers: OfferedOffer[];
  unfinishedOfferGroups: UnfinishedOfferGroup[];
}

interface OfferedOfferPreviewRequest {
  tierPriceIds: number[];
  carryOverOfferedOfferId?: number | null;
}

interface OfferedOfferRequest {
  tierPriceIds: number[];
  contactId: number;
  carryOverOfferedOfferId?: number | null;
  expiresOn?: string | null;
}

interface AppendOfferedOfferRequest {
  tierPriceIds: number[];
  replaceExisting: boolean;
}

export interface AddOfferCurrenciesRequest {
  branchId: number;
  currencyIds: number[];
  convertFromCurrencyId?: number | null;
}

export interface BranchCurrencyTarget {
  branchId: number;
  currencyId: number;
}

export interface CopyToBranchesRequest {
  targets: BranchCurrencyTarget[];
}

export const offerApi = {
  getPreview: (variantId: number) =>
    httpClient.get<OfferPreview>(`/offers/preview?variantId=${variantId}`),
  createOffer: (data: CreateOfferRequest) =>
    httpClient.post<Offer>(`/offers`, { data }),
  getOffer: (id: number) => httpClient.get<Offer>(`/offers/${id}`),
  getByVariant: (variantId: number, includeArchived = false) =>
    httpClient.get<OfferGroupSummary[]>(
      `/offers/by-variant/${variantId}?includeArchived=${includeArchived}`,
    ),
  getOffersWidget: () =>
    httpClient.get<OffersWidgetData>(`/offers/offered-offers/widget`),
  getOfferedOffersByVariant: (variantId: number) =>
    httpClient.get<OfferedOffer[]>(
      `/offers/offered-offers/by-variant/${variantId}`,
    ),
  getOfferedOffer: (id: number) =>
    httpClient.get<OfferedOffer>(`/offers/offered-offers/${id}`),
  previewOfferedOffer: (data: OfferedOfferPreviewRequest) =>
    httpClient.post<OfferedOfferPreview>(`/offers/offered-offers/preview`, {
      data,
    }),
  createOfferedOffer: (data: OfferedOfferRequest) =>
    httpClient.post<OfferedOffer>(`/offers/offered-offers`, { data }),
  appendOfferedOfferTierPrices: (id: number, data: AppendOfferedOfferRequest) =>
    httpClient.post<OfferedOffer>(`/offers/offered-offers/${id}/tier-prices`, {
      data,
    }),
  updateOffer: (id: number, data: UpdateOfferRequest) =>
    httpClient.put<Offer>(`/offers/${id}`, { data }),
  archiveOffer: (id: number) => httpClient.put<Offer>(`/offers/${id}/archive`),
  restoreOffer: (id: number) => httpClient.put<Offer>(`/offers/${id}/restore`),
  copyOffer: (id: number) => httpClient.put<Offer>(`/offers/${id}/copy`),
  getGroup: (offerGroupId: number) =>
    httpClient.get<OfferGroupDetail>(`/offers/groups/${offerGroupId}`),
  addCurrencies: (offerGroupId: number, request: AddOfferCurrenciesRequest) =>
    httpClient.post<OfferGroupDetail>(
      `/offers/groups/${offerGroupId}/currencies`,
      {
        data: request,
      },
    ),
  removeCurrency: (
    offerGroupId: number,
    branchId: number,
    currencyId: number,
  ) =>
    httpClient.delete(
      `/offers/groups/${offerGroupId}/branches/${branchId}/currencies/${currencyId}`,
    ),
  updateGroup: (offerGroupId: number, request: { ownerId: number }) =>
    httpClient.put(`/offers/groups/${offerGroupId}`, { data: request }),
  archiveGroup: (offerGroupId: number) =>
    httpClient.put<OfferGroupDetail>(`/offers/groups/${offerGroupId}/archive`),
  restoreGroup: (offerGroupId: number) =>
    httpClient.put<OfferGroupDetail>(`/offers/groups/${offerGroupId}/restore`),
  addTier: (offerGroupId: number, request: { quantity: number }) =>
    httpClient.post<OfferGroupDetail>(`/offers/groups/${offerGroupId}/tiers`, {
      data: request,
    }),
  archiveTier: (offerGroupId: number, tierId: number) =>
    httpClient.delete<OfferGroupDetail>(
      `/offers/groups/${offerGroupId}/tiers/${tierId}`,
    ),
  restoreTier: (offerGroupId: number, tierId: number) =>
    httpClient.post<OfferGroupDetail>(
      `/offers/groups/${offerGroupId}/tiers/${tierId}/restore`,
      {},
    ),
  updateTierPrice: (tierId: number, price: number | null) =>
    httpClient.put<OfferTierPrice>(`/offers/tier-prices/${tierId}`, {
      data: { price },
    }),
  copyToBranches: (offerId: number, request: CopyToBranchesRequest) =>
    httpClient.post<OfferGroupDetail>(`/offers/${offerId}/copy-to-branches`, {
      data: request,
    }),
  setMain: (offerId: number) =>
    httpClient.post<OfferGroupDetail>(`/offers/${offerId}/set-main`, {}),
  bulkUpdateTierPrices: (request: {
    tierPriceIds: number[];
    price: number | null;
  }) =>
    httpClient.put<OfferTierPrice[]>(`/offers/tier-prices/bulk`, {
      data: request,
    }),
  setMatchingInquiry: (offerGroupId: number, inquiryIds: number[]) =>
    httpClient.put(`/offers/groups/${offerGroupId}/matching-inquiry`, {
      data: { inquiryIds },
    }),
  approveGroup: (offerGroupId: number) =>
    httpClient.put<OfferGroupDetail>(`/offers/groups/${offerGroupId}/approve`),
  unapproveGroup: (offerGroupId: number) =>
    httpClient.put<OfferGroupDetail>(
      `/offers/groups/${offerGroupId}/unapprove`,
    ),
  approveOffers: (tierPriceIds: number[]) =>
    httpClient.put(`/offers/approve`, { data: { tierPriceIds } }),
  unapproveOffers: (tierPriceIds: number[]) =>
    httpClient.put(`/offers/unapprove`, { data: { tierPriceIds } }),
  createTierPriceVersion: (tierPriceId: number) =>
    httpClient.post<OfferTierPrice>(
      `/offers/tier-prices/${tierPriceId}/version`,
      {},
    ),
  getTierPriceHistory: (tierPriceId: number) =>
    httpClient.get<OfferTierPrice[]>(
      `/offers/tier-prices/${tierPriceId}/history`,
    ),
};
